import {
	creditCard_02Line,
	file_07Line,
	fileDollar,
	heartHandLine,
	home_02Line,
	logOut_01Line,
	navigationPointer_01Line,
	packageLine,
	settings_01Line,
} from "@tql/ui-components-react/untitled/icons";

import { routes } from "~/app/routes/web-routes.js";
import {
	ACCOUNT_SETTINGS_ROLES,
	REPORTS_ROLES,
	VENDOR_MANAGEMENT_ROLES,
} from "~/shared/constants/page-roles.ts";

import type { LeftNavBarItemProps } from "./components/left-navbar-item/left-navbar-item.tsx";
import { UserAvatar } from "./components/user-avatar/user-avatar.tsx";

export type NavFlags = {
	ftlQuote: boolean;
	reports: boolean;
	notifications: boolean;
	addressBook: boolean;
	autoDispatch: boolean;
	autoDispatchAdmin: boolean;
	templates: boolean;
	commodities: boolean;
	mySettings: boolean;
	accountSettings: boolean;
	admin: boolean;
	customerProfileSwitch: boolean;
	routingGuide: boolean;
	vendorManagement: boolean;
	feedbackReady: boolean;
};

export type NavContext = {
	isInternal: boolean;
	isVendor: boolean;
	canManageSharedLists: boolean;
	username: string;
	userRole: string;
	customerId: string;
	flags: NavFlags;
	onFeedbackClick: () => void;
	onLogout: () => void;
};

export const getUpperNavItems = (ctx: NavContext): LeftNavBarItemProps[] => {
	const { isInternal, isVendor, flags } = ctx;
	const items: LeftNavBarItemProps[] = [];

	if (isInternal && !isVendor) {
		items.push({
			label: "AR Dashboard",
			icon: home_02Line,
			route: routes.root,
		});
	}

	if (!isVendor) {
		items.push({
			label: "Track",
			icon: navigationPointer_01Line,
			route: routes.track,
		});

		const quoteChildren: LeftNavBarItemProps[] = [
			{ label: "Create LTL Quote", route: routes.quoteDetails },
		];
		if (!isInternal && flags.ftlQuote) {
			quoteChildren.push({
				label: "Create FTL Quote",
				route: routes.createFTLQuote,
			});
		}
		quoteChildren.push({ label: "View Quotes", route: routes.quotes });

		items.push({
			label: "Quote",
			icon: fileDollar,
			children: quoteChildren,
		});
	}

	if (isVendor) {
		items.push({
			label: "Tender",
			icon: packageLine,
			children: [{ label: "LTL Shipment", route: routes.ltlShipment }],
		});
	} else {
		const tenderChildren: LeftNavBarItemProps[] = [
			{ label: "LTL Shipment", route: routes.ltlShipment },
		];
		if (!isInternal && flags.ftlQuote) {
			tenderChildren.push({
				label: "FTL Shipment",
				route: routes.ftlShipment,
			});
		}
		items.push({
			label: "Tender",
			icon: packageLine,
			children: tenderChildren,
		});
	}

	if (!isInternal && !isVendor) {
		items.push({
			label: "Invoices",
			icon: creditCard_02Line,
			route: routes.payment,
		});
	}

	if (
		!isVendor &&
		flags.reports &&
		(isInternal || REPORTS_ROLES.has(ctx.userRole))
	) {
		items.push({
			label: "Reports",
			icon: file_07Line,
			route: routes.reports,
		});
	}

	return items;
};

const getSettingsChildren = (ctx: NavContext): LeftNavBarItemProps[] => {
	const { isInternal, userRole, customerId, flags } = ctx;
	const withCustomer = (route: string) =>
		route.replaceAll(":customerId", customerId);

	const entries: { when: boolean; items: LeftNavBarItemProps[] }[] = [
		{
			when: flags.notifications,
			items: [{ label: "Notifications", route: routes.notifications }],
		},
		{
			when: flags.addressBook,
			items: [
				{
					label: "Address Book",
					route: isInternal
						? routes.addressBook
						: withCustomer(routes.addressBookWithCustomer),
				},
			],
		},
		{
			when: flags.commodities,
			items: [{ label: "Commodities", route: routes.commodities }],
		},
		{
			when: flags.templates,
			items: [
				{
					label: "Templates",
					route: isInternal
						? routes.templates
						: withCustomer(routes.templatesWithCustomer),
				},
			],
		},
		{
			when: flags.routingGuide && isInternal,
			items: [{ label: "Routing Guides", route: routes.routingGuides }],
		},
		{
			when: flags.admin && !isInternal,
			items: [
				{
					label: "User Management",
					route: withCustomer(routes.adminWithCustomer),
				},
			],
		},
		{
			when:
				flags.vendorManagement &&
				(isInternal || VENDOR_MANAGEMENT_ROLES.has(userRole)),
			items: [
				{
					label: "Vendor Management",
					route: isInternal
						? routes.vendorManagement
						: withCustomer(routes.vendorManagementWithCustomer),
				},
			],
		},
		{
			when: flags.mySettings && !isInternal,
			items: [{ label: "My Settings", route: routes.mySettings }],
		},
		{
			when:
				flags.accountSettings &&
				!isInternal &&
				ACCOUNT_SETTINGS_ROLES.has(userRole),
			items: [{ label: "Account Settings", route: routes.accountSettings }],
		},
		{
			when: flags.autoDispatchAdmin && isInternal,
			items: [
				{
					label: "Auto Dispatch Admin",
					route: `${routes.autoDispatch}/global`,
				},
			],
		},
		{
			when: flags.autoDispatch && isInternal,
			items: [
				{ label: "Auto Dispatch", route: routes.autoDispatch, end: true },
			],
		},
	];

	return entries.flatMap((entry) => (entry.when ? entry.items : []));
};

const getVendorSettingsChildren = (ctx: NavContext): LeftNavBarItemProps[] => {
	const { flags, canManageSharedLists, customerId } = ctx;
	const items: LeftNavBarItemProps[] = [];
	if (canManageSharedLists && flags.addressBook) {
		items.push({
			label: "Address Book",
			route: routes.addressBookWithCustomer.replace(":customerId", customerId),
		});
	}
	if (canManageSharedLists && flags.commodities) {
		items.push({ label: "Commodities", route: routes.commodities });
	}
	if (flags.mySettings) {
		items.push({ label: "My Settings", route: routes.mySettings });
	}
	return items;
};

export const getLowerNavItems = (ctx: NavContext): LeftNavBarItemProps[] => {
	const { isInternal, isVendor, username, flags, onFeedbackClick, onLogout } =
		ctx;

	const userItem: LeftNavBarItemProps = {
		label: username,
		customElement: <UserAvatar name={username} />,
		customClass: "nav-bar-username",
		route:
			!isInternal && !isVendor && !flags.customerProfileSwitch
				? routes.profile
				: undefined,
	};

	const feedbackItem: LeftNavBarItemProps = {
		label: "Feedback",
		icon: heartHandLine,
		onClick: () => {
			if (flags.feedbackReady) {
				onFeedbackClick();
			}
		},
	};

	const logoutItem: LeftNavBarItemProps = {
		label: "Log out",
		icon: logOut_01Line,
		onClick: onLogout,
	};

	const settingsChildren = isVendor
		? getVendorSettingsChildren(ctx)
		: getSettingsChildren(ctx);

	return [
		userItem,
		{ label: "Settings", icon: settings_01Line, children: settingsChildren },
		feedbackItem,
		logoutItem,
	];
};
